import { useState } from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select.js';
import { PdfCompareView } from './PdfCompareView.js';
import { fmtNum } from '../../utils.js';
import type { Batch, BatchFile, BatchWarning, ClientFund, FileEdits } from './pdfBatchApi.js';

/**
 * What was read from each file, before any of it becomes a number a client sees.
 *
 * This was a table, one row per file, and it did not work: the fields you have
 * to ACT on — which portfolio, and what moved in and out — were squeezed between
 * columns you only read, the two money boxes carried no labels, and the row was
 * cramped even with a single file. A month is usually a handful of files, so
 * each one gets a card with its own decisions laid out.
 */
export function PdfReview({
  batch,
  busy,
  onEdit,
  onRemove,
}: {
  batch: Batch;
  busy: boolean;
  onEdit: (fileId: string, edits: FileEdits) => void;
  onRemove: (fileId: string) => void;
}) {
  const [comparing, setComparing] = useState<BatchFile | null>(null);
  return (
    <div className="grid gap-4">
      {comparing && (
        <PdfCompareView batchId={batch.batch_id} file={comparing} onClose={() => setComparing(null)} />
      )}

      {batch.warnings.map((w) => (
        <Notice key={w.code + w.message} warning={w} />
      ))}

      {batch.files.map((f) => (
        <FileCard
          key={f.file_id}
          file={f}
          funds={batch.client_funds}
          busy={busy}
          onEdit={onEdit}
          onRemove={onRemove}
          onCompare={() => setComparing(f)}
        />
      ))}
    </div>
  );
}

function FileCard({
  file,
  funds,
  busy,
  onEdit,
  onRemove,
  onCompare,
}: {
  file: BatchFile;
  funds: ClientFund[];
  busy: boolean;
  onEdit: (fileId: string, edits: FileEdits) => void;
  onRemove: (fileId: string) => void;
  onCompare: () => void;
}) {
  const failed = file.status === 'error';
  const blocking = file.warnings.some((w) => w.level === 'error');

  return (
    <div
      className={`rounded-xl border bg-card p-4 ${blocking ? 'border-destructive/50' : 'border-border'}`}
    >
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <span className="font-medium">{file.filename}</span>
        <span className="flex gap-1.5">
          {!failed && (
            <button
              type="button"
              disabled={busy}
              onClick={onCompare}
              className="rounded-md border border-border px-2.5 py-1 text-xs hover:bg-muted disabled:opacity-50"
            >
              בדיקה מול הקובץ
            </button>
          )}
          <button
            type="button"
            aria-label={`הסרת ${file.filename}`}
            disabled={busy}
            onClick={() => onRemove(file.file_id)}
            className="rounded-md border border-border px-2.5 py-1 text-xs text-muted-foreground hover:bg-muted disabled:opacity-50"
          >
            הסרה
          </button>
        </span>
      </div>

      {failed ? (
        <p className="text-sm text-destructive">{file.error ?? 'החילוץ נכשל'}</p>
      ) : (
        <div className="grid gap-4">
          <div className="grid gap-4 sm:grid-cols-[minmax(11rem,14rem)_1fr] sm:items-start">
            <Field label="לאיזה תיק זה שייך?">
              <FundPicker file={file} funds={funds} busy={busy} onEdit={onEdit} />
            </Field>
            <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm sm:grid-cols-3">
              <Fact label="תאריך הדוח" value={fmtDate(file.report_date)} />
              <Fact label='סה"כ' value={file.total_value == null ? '—' : fmtNum(file.total_value)} strong />
              <Fact label="ניירות ערך" value={String(file.positions_count)} />
            </dl>
          </div>

          <Flows file={file} busy={busy} onEdit={onEdit} />

          {file.warnings.map((w) => (
            <Notice key={w.code} warning={w} file={file} busy={busy} onEdit={onEdit} />
          ))}
        </div>
      )}
    </div>
  );
}

/** Israeli convention, and never wrapped mid-date. */
const fmtDate = (iso: string | null) => {
  if (!iso) return '—';
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
};

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block text-sm">
      <span className="mb-1 block text-muted-foreground">{label}</span>
      {children}
    </label>
  );
}

function Fact({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div>
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className={`tabular-nums whitespace-nowrap ${strong ? 'text-base font-semibold' : ''}`}>{value}</dd>
    </div>
  );
}

const NEW_FUND = '__new__';

/**
 * The portfolio is CHOSEN, never inferred from the filename. A filename once
 * became a second portfolio for a real client and their balance was counted
 * twice; that is the whole reason this is a list and not a text box.
 */
function FundPicker({
  file,
  funds,
  busy,
  onEdit,
}: {
  file: BatchFile;
  funds: ClientFund[];
  busy: boolean;
  onEdit: (fileId: string, edits: FileEdits) => void;
}) {
  const known = funds.some((f) => f.fund === file.fund);
  const [creating, setCreating] = useState(!known && file.fund_confirmed);
  const [draft, setDraft] = useState(file.fund);

  if (creating) {
    return (
      <span className="flex flex-col gap-1">
        <input
          value={draft}
          disabled={busy}
          autoFocus
          onChange={(e) => setDraft(e.target.value)}
          onBlur={() => draft.trim() && onEdit(file.file_id, { fund: draft.trim(), fundConfirmed: true })}
          onKeyDown={(e) => e.key === 'Enter' && e.currentTarget.blur()}
          aria-label={`שם התיק החדש עבור ${file.filename}`}
          className="w-full rounded-md border border-input bg-popover px-3 py-2 text-sm"
        />
        <button
          type="button"
          disabled={busy}
          onClick={() => { setCreating(false); setDraft(file.fund); }}
          className="self-start text-xs text-muted-foreground underline hover:text-foreground"
        >
          בחירה מהרשימה
        </button>
      </span>
    );
  }

  return (
    <Select
      dir="rtl"
      value={known ? file.fund : undefined}
      disabled={busy}
      onValueChange={(v) => (v === NEW_FUND ? setCreating(true) : onEdit(file.file_id, { fund: v }))}
    >
      <SelectTrigger className="w-full bg-popover" aria-label={`התיק עבור ${file.filename}`}>
        <SelectValue placeholder="בחרו תיק…" />
      </SelectTrigger>
      <SelectContent>
        {funds.map((f) => (
          <SelectItem key={f.fund} value={f.fund}>
            {f.fund}
            {f.last_date && <span className="text-muted-foreground"> · עד {fmtDate(f.last_date)}</span>}
          </SelectItem>
        ))}
        <SelectItem value={NEW_FUND}>+ תיק חדש…</SelectItem>
      </SelectContent>
    </Select>
  );
}

/**
 * Deposits and withdrawals, each with its own name on it. They were two bare
 * boxes under one shared heading, which is unreadable — in a right-to-left
 * layout you could not tell which number was which.
 */
function Flows({
  file,
  busy,
  onEdit,
}: {
  file: BatchFile;
  busy: boolean;
  onEdit: (fileId: string, edits: FileEdits) => void;
}) {
  // Pre-filled values come straight from the document and are never typed into,
  // so they have to arrive already readable rather than waiting for a blur.
  const fmt = (n: number | null) => (n === null ? '' : n.toLocaleString('en-US'));
  const [dep, setDep] = useState(fmt(file.deposits));
  const [wd, setWd] = useState(fmt(file.withdrawals));
  const invalid = (v: string) => v.trim() !== '' && !Number.isFinite(Number(v.replace(/,/g, '')));
  const num = (v: string) => (v.trim() === '' ? 0 : Number(v.replace(/,/g, '')));

  if (file.flows_confirmed) {
    return (
      <div className="flex flex-wrap items-center gap-x-6 gap-y-1 rounded-lg bg-muted/40 px-3 py-2 text-sm">
        <span className="text-muted-foreground">תנועות בחודש</span>
        <span className="tabular-nums">הפקדות <strong className="text-success">{fmtNum(file.deposits ?? 0)}</strong></span>
        <span className="tabular-nums">משיכות <strong>{fmtNum(file.withdrawals ?? 0)}</strong></span>
        <button
          type="button"
          disabled={busy}
          onClick={() => onEdit(file.file_id, { flowsConfirmed: false })}
          className="text-xs text-muted-foreground underline hover:text-foreground disabled:opacity-50"
        >
          שינוי
        </button>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-border bg-muted/30 px-3 py-2.5">
      <p className="mb-2 text-sm font-medium">תנועות בחודש</p>
      <div className="flex flex-wrap items-end gap-3">
        <MoneyField label="הפקדות" value={dep} onChange={setDep} invalid={invalid(dep)} busy={busy} fund={file.fund} />
        <MoneyField label="משיכות" value={wd} onChange={setWd} invalid={invalid(wd)} busy={busy} fund={file.fund} />
        <button
          type="button"
          disabled={busy || invalid(dep) || invalid(wd)}
          onClick={() => onEdit(file.file_id, {
            deposits: num(dep), withdrawals: num(wd), flowsConfirmed: true,
          })}
          className="rounded-md border border-border bg-background px-3 py-2 text-sm hover:bg-muted disabled:opacity-50"
        >
          {dep.trim() === '' && wd.trim() === '' ? 'לא היו תנועות' : 'אישור'}
        </button>
      </div>
    </div>
  );
}

function MoneyField({
  label, value, onChange, invalid, busy, fund,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  invalid: boolean;
  busy: boolean;
  fund: string;
}) {
  return (
    <label className="text-sm">
      <span className="mb-1 block text-xs text-muted-foreground">{label}</span>
      <input
        value={value}
        disabled={busy}
        inputMode="decimal"
        placeholder="0"
        aria-label={`${label} עבור ${fund}`}
        onChange={(e) => onChange(e.target.value)}
        // Separators on blur: readable at rest, plain while being typed into.
        onBlur={() => {
          const n = Number(value.replace(/,/g, ''));
          if (value.trim() !== '' && Number.isFinite(n)) onChange(n.toLocaleString('en-US'));
        }}
        onFocus={() => onChange(value.replace(/,/g, ''))}
        className={`w-32 rounded-md border bg-popover px-3 py-2 text-sm tabular-nums ${invalid ? 'border-destructive' : 'border-input'}`}
      />
    </label>
  );
}

const TONE: Record<BatchWarning['level'], string> = {
  error: 'border-destructive/40 bg-destructive/10 text-destructive',
  warn: 'border-warning/40 bg-warning/10 text-warning',
  info: 'border-border bg-muted/40 text-muted-foreground',
};

function Notice({
  warning, file, busy, onEdit,
}: {
  warning: BatchWarning;
  file?: BatchFile;
  busy?: boolean;
  onEdit?: (fileId: string, edits: FileEdits) => void;
}) {
  const canConfirmNewFund = warning.code === 'fund_unknown' && file && onEdit;
  return (
    <div className={`flex flex-wrap items-center gap-2 rounded-lg border px-3 py-2 text-sm ${TONE[warning.level]}`}>
      <span>{warning.message}</span>
      {canConfirmNewFund && (
        <button
          type="button"
          disabled={busy}
          onClick={() => onEdit(file.file_id, { fundConfirmed: true })}
          className="rounded border border-current/40 px-2 py-0.5 text-xs font-semibold hover:bg-background/40 disabled:opacity-50"
        >
          זה תיק חדש — אשר
        </button>
      )}
    </div>
  );
}
